import { useEffect, useState } from "react";
import { geoSuggest, geoRetrieve } from "../api";
import { findPlaceSuggestion, isPlaceProbeCandidate, normalizePlaceQuery } from "./placeSuggestion";

// Same settle time as the search itself — the probe rides the identical rhythm, so the
// chip appears together with the results it annotates.
const PROBE_DEBOUNCE_MS = 400;

// Dismissals are per normalized query and live in sessionStorage: a dismissed suggestion
// shouldn't nag again this sitting, but shouldn't be suppressed forever either.
const DISMISSED_STORAGE_KEY = "veft.searchPlaceChipDismissed";

function loadDismissed() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(DISMISSED_STORAGE_KEY) || "null");
    if (Array.isArray(parsed)) return new Set(parsed);
  } catch { /* malformed / unavailable storage */ }
  return new Set();
}

function persistDismissed(set) {
  try {
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...set]));
  } catch { /* storage unavailable / quota — non-fatal */ }
}

function newSession() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// One geoSuggest per unique normalized query per visit — module-level so a back-navigation
// remount doesn't re-bill already-probed queries. Values may be null: "probed, no hit".
const probeCache = new Map();
// Dedicated Mapbox billing session for chip probes, separate from the autocomplete's.
let probeSession = newSession();

/**
 * Watches the free-text search query and, when it looks like a bare place name ("Berlin",
 * "koln", "new york"), probes the geocoder for an exact-name match to offer as a one-click
 * structured filter. Returns { suggestion, retrieve, dismiss }:
 *  - suggestion: { name, mapboxId, featureType, … } or null — null while disabled (a geo
 *    filter is active), the query fails the gates, it was dismissed, or nothing matched.
 *  - retrieve(): resolves the suggestion to a full GeoLocation; null on failure — the
 *    caller treats that as a silent no-op and the chip stays.
 *  - dismiss(): suppresses the suggestion for this query for the rest of the session.
 */
export function usePlaceSuggestion(query, { disabled = false } = {}) {
  // Bumped when an async probe lands; the probe result itself lives in the module cache.
  const [, setProbeTick] = useState(0);
  const [dismissed, setDismissed] = useState(loadDismissed);

  // The visible suggestion is DERIVED, never set from the effect: current query + gates +
  // whatever the cache holds. A probe that lands late merely fills the cache for the query
  // it was sent for — it can never surface a stale chip (the staleness guard is structural).
  const normalized = normalizePlaceQuery(query);
  const gated = !disabled && isPlaceProbeCandidate(normalized) && !dismissed.has(normalized);
  const suggestion = gated ? probeCache.get(normalized) ?? null : null;
  const needsProbe = gated && !probeCache.has(normalized);

  useEffect(() => {
    if (!needsProbe) return undefined;
    // Retyping changes `normalized`, which clears the pending probe — a true debounce.
    const handle = setTimeout(async () => {
      let hit = null;
      try {
        hit = findPlaceSuggestion(normalized, await geoSuggest(normalized, probeSession));
      } catch { /* probe is best-effort — no chip beats an error */ }
      probeCache.set(normalized, hit); // null too: "probed, no hit" is never re-billed
      setProbeTick((t) => t + 1); // re-render so the derived suggestion can surface
    }, PROBE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [needsProbe, normalized]);

  async function retrieve() {
    if (!suggestion?.mapboxId) return null;
    try {
      return (await geoRetrieve(suggestion.mapboxId, probeSession)) ?? null;
    } catch {
      return null; // silent no-op — the chip stays, the user can retry
    } finally {
      probeSession = newSession(); // a /retrieve ends the billing session
    }
  }

  function dismiss() {
    if (!suggestion) return;
    const next = new Set(dismissed);
    next.add(normalizePlaceQuery(suggestion.name));
    persistDismissed(next);
    setDismissed(next);
  }

  return { suggestion, retrieve, dismiss };
}
